import { formatPerformanceRatioLabel, signedCommaDecimal } from "@normax-performance/shared-format";

/**
 * Shared Analytics/Dashboard number formatting — extracted from
 * `features/analytics/format.ts` during Dashboard Slice C
 * (docs/NORMAX_PERFORMANCE_DASHBOARD_SLICE_C_IMPLEMENTATION_REPORT.md) once
 * Dashboard needed the exact same percent/minutes/trend formatting as
 * Analytics. Lives in `lib/format` (the existing shared formatting home,
 * alongside `intl-format.ts`) rather than one feature importing another
 * feature's internals.
 *
 * Deliberately reuses two DIFFERENT existing conventions rather than
 * inventing a third:
 *
 * - Performance/deviation/trend values: ALWAYS comma-decimal, regardless of
 *   UI language — the exact precedent already established by
 *   `dailyEntryHistoryFormat.ts` (Daily Entry Audit History), which
 *   comma-formats these business-calculation values unconditionally.
 * - Minutes/counts: locale-aware thousands grouping via `Intl.NumberFormat`
 *   — the exact precedent already established by `formatCount`
 *   (`./intl-format.ts`).
 *
 * Never reimplements rounding — `performancePercentLabel` arrives from the
 * API already rounded to the locked 2-decimal HALF_AWAY_FROM_ZERO contract
 * (`CalculationService.formatPercentage`); this module only re-punctuates
 * it for display. `trendPercentagePoints` arrives as a plain already-
 * business-computed number (Decimal arithmetic happened server-side); this
 * module only rounds it to the same 2-decimal display contract via
 * `toFixed`, safe at this stage because it is presentation rounding of an
 * already-final value, not a business calculation.
 */

/** `percentLabel` is the API's already-rounded `performancePercentLabel` (e.g. "96.82"), or null when no denominator exists. */
export function formatAnalyticsPercent(percentLabel: string | null, _intlLocale: string): string {
  return formatPerformanceRatioLabel(percentLabel) ?? "—";
}

/** `minutes` is a raw (possibly unrounded) decimal string from the API — rounded to the nearest whole minute, then grouped per locale. */
export function formatAnalyticsMinutes(minutes: string, intlLocale: string): string {
  const rounded = Math.round(Number(minutes));
  return new Intl.NumberFormat(intlLocale).format(rounded);
}

/**
 * For large AGGREGATE minute totals only — the dashboard's and Analytics'
 * own top-level "Ukupno evidentirano vreme"/"Ukupno vreme" figures, summed
 * across every worker/day in view. Reads "270 h" instead of forcing the
 * reader to mentally divide "16.200" by 60. Never used for a per-row/
 * per-entity value (a single worker's, product's, or norm's minutes) —
 * those stay in minutes via `formatAnalyticsMinutes`, both because they are
 * usually small enough that minutes stay precise and useful, and because
 * their own column headers (e.g. "Normirani min") already commit to the
 * minutes unit.
 */
export function formatAnalyticsHoursMinutes(minutes: string, intlLocale: string): string {
  const rounded = Math.round(Number(minutes));
  if (Math.abs(rounded) < 60) return `${new Intl.NumberFormat(intlLocale).format(rounded)} min`;
  const sign = rounded < 0 ? "-" : "";
  const abs = Math.abs(rounded);
  const hours = Math.floor(abs / 60);
  const remainderMinutes = abs % 60;
  const hoursDisplay = new Intl.NumberFormat(intlLocale).format(hours);
  return remainderMinutes > 0 ? `${sign}${hoursDisplay} h ${remainderMinutes} min` : `${sign}${hoursDisplay} h`;
}

/** `trendPercentagePoints` is the API's raw trend delta (already Decimal-computed server-side), or null when no valid previous-period denominator exists. */
export function formatAnalyticsTrend(trendPercentagePoints: number | null): string {
  if (trendPercentagePoints === null) return "—";
  return `${signedCommaDecimal(trendPercentagePoints.toFixed(2))} p.p.`;
}

/** Note: a bare comma-decimal conversion without a trailing " p.p."/"%" is already available as `dotToCommaDecimal`, re-exported unchanged via `@normax-performance/shared-format` at this same `lib/format` barrel — not re-exported a second time here to avoid an ambiguous duplicate `export *` binding. */
